import { useState, useEffect } from "react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useToast } from '../components/userToast'
import { BASE_URL } from "../baseurl";

export default function FeaturedProducts({ content }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { addToCart } = useCart();
  const toast = useToast();
  const [status, setStatus] = useState({});

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/items/featuredItems`);
        setProducts(res.data.items);
      } catch (err) {
        console.error("Failed to fetch items:", err);
        setError("Failed to load products");
        toast({
          variant: "error",
          title: "Couldn't load products",
          description:
            err.response?.data?.message || "Something went wrong while fetching items",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  const handleAddToCart = (product) => {
    if (status[product._id]) return;
    setStatus((prev) => ({ ...prev, [product._id]: "loading" }));
    setTimeout(() => {
      addToCart(product);
      setStatus((prev) => ({ ...prev, [product._id]: "added" }));
      toast({
        variant: "success",
        title: "Added to cart",
        description: `${product.title} has been added to your cart.`,
      });
    }, 900);
  };

  return (
    <section className="w-full bg-gray-50 pt-[60px] pb-[60px] px-[clamp(24px,5vw,80px)]">
      <div className="max-w-[1200px] mx-auto">
        <h2 className="text-gray-800 font-bold text-center text-4xl mb-10">
          {content?.featuredProductsTitle || "Featured Products"}
        </h2>

        {loading && (
          <p className="text-center text-gray-500 py-10">Loading products...</p>
        )}

        {error && (
          <p className="text-center text-red-500 py-10">{error}</p>
        )}

        {!loading && !error && products.length === 0 && (
          <p className="text-center text-gray-500 py-10">No products available yet.</p>
        )}

        {!loading && !error && products.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {products.map((product) => {
              const state = status[product._id];
              return (
                <div key={product._id}>
                  <div className="bg-white rounded-lg flex items-center justify-center w-full h-[260px] overflow-hidden">
                    {product.image ? (
                      <img
                        src={`${BASE_URL.replace(/\/api$/, "")}${product.image}`}
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg
                        width="60"
                        height="60"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="text-gray-300"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
                        <circle cx="9" cy="9" r="1.5" fill="currentColor" />
                        <path
                          d="M21 15l-5-5-9 9"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>

                  <h3 className="text-gray-800 font-medium text-[17px] mt-4">
                    {product.title}
                  </h3>

                  <p className="text-gray-600 font-semibold text-[15px] mt-1.5 mb-3.5">
                    ${Number(product.price).toFixed(2)}
                  </p>

                  <button
                    onClick={() => handleAddToCart(product)}
                    disabled={state === "loading" || state === "added"}
                    className="flex items-center justify-center gap-2 rounded-full border border-teal-500 text-teal-600 hover:bg-teal-500 hover:text-white transition-colors text-[17px] px-6 py-[11px] w-full disabled:cursor-default"
                  >
                    {state === "loading" && (
                      <svg
                        className="animate-spin"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeDasharray="42"
                          strokeDashoffset="30"
                          fill="none"
                        />
                      </svg>
                    )}

                    <span>ADD TO CART</span>

                    {state === "added" && (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 13l4 4L19 7"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}